import React, { useState } from "react";
import { LuCheck, LuMail } from "react-icons/lu";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../utils/firebase";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const inputCls = "w-full border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-ring text-foreground placeholder:text-[var(--color-muted-foreground)] bg-card text-sm transition-colors";
const labelCls = "block text-xs font-bold text-[var(--color-muted-foreground)] uppercase tracking-wider mb-1.5";

export default function ForgotPassword() {
  const { user } = useAuth();
  const [email,     setEmail]     = useState("");
  const [loading,   setLoading]   = useState(false);
  const [sent,      setSent]      = useState(false);
  const [error,     setError]     = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await sendPasswordResetEmail(auth, email);
      setSent(true);
    } catch (err) {
      if (err.code === "auth/user-not-found" || err.code === "auth/invalid-email") {
        setSent(true);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background font-sans flex">

      {/* Left — dark branding panel (always dark, no change needed) */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] bg-brand-navy p-14">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="bg-white rounded-lg p-1.5 flex-shrink-0">
            <img src="/images/croppedLogo.png" alt="Khoja" className="h-9 w-auto" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="text-white font-black text-[16px] tracking-tight" style={{ fontFamily: "Lexend, sans-serif" }}>Khoja</span>
            <span className="text-brand-amber font-bold text-[9px] tracking-[0.2em] uppercase mt-0.5" style={{ fontFamily: "Lexend, sans-serif" }}>Distributions</span>
          </div>
        </Link>
        <div>
          <span className="inline-block bg-brand-amber/20 border border-brand-amber/30 text-brand-amber text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-6">
            Account Recovery
          </span>
          <h1 className="text-4xl font-black text-white leading-tight tracking-tight mb-4">
            Reset your password
          </h1>
          <p className="text-slate-400 text-base leading-relaxed mb-10">
            Enter your registered email and we'll send you a secure link to set a new password. The link expires after 1 hour.
          </p>
          <div className="space-y-3">
            {[
              "Link sent to your registered email",
              "Expires automatically after 1 hour",
              "No account changes until you click the link",
              "Contact support if you've lost access to your email",
            ].map(item => (
              <div key={item} className="flex items-center gap-3 text-sm text-slate-300">
                <div className="w-5 h-5 rounded-full bg-brand-amber/20 border border-brand-amber/40 flex items-center justify-center flex-shrink-0">
                  <LuCheck size={12} className="text-brand-amber" strokeWidth={3} />
                </div>
                {item}
              </div>
            ))}
          </div>
        </div>
        <p className="text-slate-600 text-xs">© {new Date().getFullYear()} Khoja Distributions · DFW, Texas</p>
      </div>

      {/* Right — form */}
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="lg:hidden mb-8">
            <Link to="/"><img src="/images/croppedLogo.png" alt="Khoja" className="h-8 w-auto dark:brightness-0 dark:invert" /></Link>
          </div>

          {sent ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-green-50 border border-green-200 dark:bg-green-950/30 dark:border-green-900/50 flex items-center justify-center mx-auto mb-6">
                <LuMail size={32} className="text-green-500 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-black text-foreground tracking-tight mb-2">Check your inbox</h2>
              <p className="text-[var(--color-muted-foreground)] text-sm mb-2">
                If <strong className="text-foreground">{email}</strong> is registered, you'll receive a reset link shortly.
              </p>
              <p className="text-[var(--color-muted-foreground)] text-xs mb-8">
                Don't see it? Check your spam folder or wait a few minutes.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => { setSent(false); setEmail(""); }}
                  className="w-full bg-brand-navy hover:bg-brand-amber transition-colors text-white font-bold py-3.5 rounded-xl text-sm"
                >
                  Try a different email
                </button>
                <Link
                  to="/login"
                  className="block w-full text-center text-sm text-foreground font-bold hover:text-accent transition-colors py-2"
                >
                  ← Back to Sign In
                </Link>
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-3xl font-black text-foreground tracking-tight mb-1">Forgot password?</h2>
              <p className="text-[var(--color-muted-foreground)] text-sm mb-8">
                Enter your account email and we'll send you a reset link.
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-950/30 dark:border-red-900/50 dark:text-red-400 text-sm px-4 py-3 rounded-xl mb-5">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className={labelCls}>Email Address</label>
                  <input
                    type="email"
                    className={inputCls}
                    placeholder="you@business.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand-navy hover:bg-brand-amber disabled:opacity-50 transition-colors text-white font-bold py-3.5 rounded-xl"
                >
                  {loading ? "Sending reset link…" : "Send Reset Link"}
                </button>
              </form>

              {!user && (
                <>
                  <p className="text-sm text-center mt-6 text-[var(--color-muted-foreground)]">
                    Remember your password?{" "}
                    <Link to="/login" className="text-foreground font-bold hover:text-accent transition-colors">
                      Sign In
                    </Link>
                  </p>
                  <p className="text-sm text-center mt-2 text-[var(--color-muted-foreground)]">
                    Don't have an account?{" "}
                    <Link to="/register" className="text-foreground font-bold hover:text-accent transition-colors">
                      Apply for Wholesale Access
                    </Link>
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
